<script>
    import Swiper from "@/plugins/swiper-bundle";
    import { isMobile, lazyLoad } from "@/utils";
    import BaseButton from "@main/packages/BaseButton";
    import HomePlantsCard from "@igarden/components/HomePlantsCard";
    import TransitionContainer from "@/components/TransitionContainer";

    export default {
        name: "HomePlants",
        components: { TransitionContainer, BaseButton, HomePlantsCard },
        data() {
            return {
                slider: null,
                sliderActiveIndex: 1,
                isVisibleAllPlants: false,
                isDisabledHover: false,
                plantsList: [],
            };
        },
        computed: {
            moreButtonText() {
                return this.isVisibleAllPlants ? "Свернуть список" : "Смотреть все растения";
            },
            plantsListStart() {
                return this.plantsList.slice(0, 9);
            },
            plantsListEnd() {
                return this.plantsList.slice(9, this.plantsList.length);
            },
        },
        mounted() {
            this.getPlants();
        },
        methods: {
            getPlants() {
                this.$store.optionalApi.getTextCards("home-plants").then(({ items }) => {
                    this.plantsList = items;
                    this.$nextTick(() => {
                        lazyLoad();
                        this.initMobileSlider();
                    });
                });
            },
            initMobileSlider() {
                if (isMobile(992)) {
                    this.slider = new Swiper(".js-home-plants-slider", {
                        spaceBetween: 20,
                        direction: "horizontal",
                        grabCursor: true,
                        lazy: {
                            loadOnTransitionStart: true,
                            loadPrevNext: true,
                        },
                    });

                    this.slider.on("slideChange", (slider) => {
                        this.sliderActiveIndex = slider.activeIndex + 1;
                    });
                }
            },
            togglePlants() {
                this.isDisabledHover = true;
                if (this.isVisibleAllPlants) {
                    this.isVisibleAllPlants = false;
                    this.isDisabledHover = false;
                    this.scrollTo();
                } else {
                    this.isVisibleAllPlants = true;
                    setTimeout(() => {
                        this.isDisabledHover = false;
                    }, 500);
                }
            },

            scrollTo() {
                const el = this.$refs.homePlants;
                const top = el.getBoundingClientRect().top + window.scrollY - 55;
                window.scrollTo({
                    top,
                    behavior: "smooth",
                });
            },
        },
    };
</script>

<template>
    <section class="home-plants" ref="homePlants" id="plants">
        <header class="home-plants__header">
            <h2 class="home-plants__title page-title">Растения для умного сада</h2>
            <div class="home-plants__subtitle">
                Какие ягоды, травы и цветы можно выращивать в iGarden
            </div>
        </header>

        <div v-if="plantsList.length > 0" class="home-plants__list mobile-hidden">
            <div class="home-plants__inner">
                <div
                    class="home-plants__item"
                    v-for="plant in plantsListStart"
                    :key="plant.id"
                >
                    <home-plants-card :plant="plant"></home-plants-card>
                </div>
            </div>
            <transition-container :show="isVisibleAllPlants">
                <div class="home-plants__inner" :class="{ 'is-disabled': isDisabledHover }">
                    <div
                        class="home-plants__item"
                        v-for="plant in plantsListEnd"
                        :key="plant.id"
                    >
                        <home-plants-card :plant="plant"></home-plants-card>
                    </div>
                    <div class="home-plants__notification">
                        Указано время первого урожая. Сроки зависят от сорта, возраста семян,
                        температуры и состава питательного раствора
                    </div>
                </div>
            </transition-container>
        </div>
        <div v-else class="home-plants__preloader">
            <preloader :show="true" lg-size></preloader>
        </div>

        <div v-if="plantsList.length > 0" class="home-plants__slider desktop-hidden">
            <div class="swiper-container js-home-plants-slider">
                <div class="swiper-wrapper">
                    <div
                        class="swiper-slide home-plants__slide"
                        v-for="plant in plantsList"
                        :key="plant.id"
                    >
                        <home-plants-card :plant="plant" is-slide></home-plants-card>
                    </div>
                </div>
            </div>
            <div class="home-plants__counter">
                {{ sliderActiveIndex }} / {{ plantsList.length }}
            </div>
        </div>

        <base-button
            v-if="plantsList.length > 0"
            class="home-plants__more mobile-hidden"
            size="md"
            @click="togglePlants"
        >
            {{ moreButtonText }}
        </base-button>
    </section>
</template>

<style lang="scss">
    .home-plants {
        padding: 24px 16px 32px;
        background-color: #ecf8f5;
        border-radius: var(--border-lg);
        @include bp($bp-desktop-sm) {
            position: relative;
            padding: 44px 40px;
            border-radius: var(--border-xl);
        }
    }

    .home-plants__header {
        text-align: center;
        @include bp($bp-desktop-sm) {
            margin-bottom: 32px;
        }
    }

    .home-plants__title {
        margin-bottom: 12px;
        @include bp($bp-desktop-sm) {
            margin-bottom: 16px;
        }
    }

    .home-plants__subtitle {
        font-size: 14px;
        font-weight: 400;
        line-height: 20px;
        @include bp($bp-desktop-sm) {
            font-size: 18px;
            line-height: 30px;
        }
    }

    .home-plants__list {
        @include bp($bp-desktop-sm) {
            margin: 0 -8px -16px;
            .transition-container {
                --transition-duration--slide-down: 0.5s;
            }
        }
    }

    .home-plants__preloader {
        position: relative;
        min-height: 100px;
    }

    .home-plants__inner {
        @include bp($bp-desktop-sm) {
            display: flex;
            flex-wrap: wrap;
            &.is-disabled {
                pointer-events: none;
            }
        }
    }

    .home-plants__item {
        @include bp($bp-desktop-sm) {
            flex-basis: calc(100% / 3);
            max-width: calc(100% / 3);
            min-height: 125px;
            padding: 0 8px 16px;
        }
    }

    .home-plants__notification {
        display: none;
        @include bp($bp-desktop-sm) {
            display: block;
            width: 100%;
            margin: 0 8px;
            padding: 20px;
            color: var(--danger);
            background-color: #fff;
            border: 2px solid #ffe9e4;
            border-radius: var(--border-md);
            text-align: center;
        }
    }

    .home-plants__more {
        @include bp($bp-desktop-sm) {
            width: 100%;
            max-width: 380px;
            margin: 40px auto 0;
            font-family: var(--secondary-font);
        }
    }

    .home-plants__slider {
        margin: 0 -16px;
        .swiper-container {
            overflow: visible;
        }
    }

    .home-plants__slide {
        padding: 24px 16px;
        &.swiper-slide {
            height: auto;
        }
    }

    .home-plants__counter {
        font-size: 16px;
        font-weight: 700;
        line-height: 20px;
        text-align: center;
        @include bp($bp-desktop-sm) {
            display: none;
        }
    }
</style>
